import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    buildDebiasedRenderPieces,
    type DebiasedRenderPiece,
} from "@/utils/logSequenceMatcherDiff";

function ChangeMark({
    piece,
}: {
    piece: Extract<DebiasedRenderPiece, { kind: "mark" }>;
}) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLSpanElement>(null);
    const tipId = useId();
    const [tipPos, setTipPos] = useState<{
        top: number;
        left: number;
    } | null>(null);

    useLayoutEffect(() => {
        if (!open || !wrapRef.current) {
            setTipPos(null);
            return;
        }
        const el = wrapRef.current;
        const r = el.getBoundingClientRect();
        const margin = 8;
        let left = r.left;
        const maxW = Math.min(320, window.innerWidth - margin * 2);
        if (left + maxW > window.innerWidth - margin) {
            left = window.innerWidth - margin - maxW;
        }
        left = Math.max(margin, left);
        setTipPos({
            top: r.bottom + 6,
            left,
        });
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: PointerEvent) => {
            if (
                wrapRef.current &&
                !wrapRef.current.contains(e.target as Node)
            ) {
                const t = e.target as HTMLElement | null;
                if (t?.closest?.("[data-debias-tip]")) return;
                setOpen(false);
            }
        };
        document.addEventListener("pointerdown", onDoc);
        return () => document.removeEventListener("pointerdown", onDoc);
    }, [open]);


    return (
        <span ref={wrapRef} className="relative inline align-baseline">
            <mark
                className="cursor-pointer rounded-sm bg-amber-200/95 px-0.5 text-inherit underline decoration-amber-600/50 decoration-2 underline-offset-2 ring-1 ring-amber-400/70 transition hover:bg-amber-200"
                onMouseEnter={() => setOpen((v) => !v)}
                onMouseLeave={() => setOpen((v) => !v)}
            >
                {piece.text}
            </mark>
            {open &&
                tipPos &&
                createPortal(
                    <span
                        id={tipId}
                        data-debias-tip
                        role="tooltip"
                        style={{
                            position: "fixed",
                            top: tipPos.top,
                            left: tipPos.left,
                            zIndex: 9999,
                            maxWidth: "min(20rem, calc(100vw - 1rem))",
                        }}
                        className="min-w-[12rem] rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs leading-snug text-gray-800 shadow-lg"
                    >
                        {piece.change === "add" ? (
                            <span className="block text-green-800">
                                <span className="font-semibold text-gray-600">
                                    Προστέθηκε:{" "}
                                </span>
                                «{piece.text}»
                            </span>
                        ) : (
                            <>
                                <span className="block text-red-800">
                                    <span className="font-semibold text-gray-600">
                                        Πριν:{" "}
                                    </span>
                                    «{piece.removed}»
                                </span>
                                <span className="mt-1 block border-t border-gray-100 pt-1 text-green-800">
                                    <span className="font-semibold text-gray-600">
                                        Μετά:{" "}
                                    </span>
                                    «{piece.text}»
                                </span>
                            </>
                        )}
                    </span>,
                    document.body
                )}
        </span>
    );
}

interface DebiasedHighlightedTextProps {
    debiased: string;
    original: string;
}

/**
 * Εμφανίζει το debiased κείμενο με επισήμανση στις αλλαγές· πάτημα/κλικ ανοίγει την επεξήγηση.
 */
export default function DebiasedHighlightedText({
    debiased,
    original,
}: DebiasedHighlightedTextProps) {
    const pieces = buildDebiasedRenderPieces(original, debiased);

    return (
        <span className="whitespace-pre-wrap break-words">
            {pieces.map((piece, i) => {
                const key = `${i}-${piece.kind}-${piece.kind === "plain" ? piece.text.slice(0, 24) : piece.text.slice(0, 16)}`;
                if (piece.kind === "plain") {
                    return <span key={key}>{piece.text}</span>;
                }
                return <ChangeMark key={key} piece={piece} />;
            })}
        </span>
    );
}